import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PrintSettings, Template, UserPreferences } from '../api/types';
import {
  loadPreferences,
  loadTemplatesForTable,
  savePreferences,
  saveTemplatesForTable,
} from './persistence';
import { MAX_TEMPLATES_PER_TABLE } from '../utils/constants';

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export interface UseTemplatesResult {
  templates: Template[];
  selectedId: string | null;
  /** Replace last-selected for this table (no field/setting side-effect). */
  setSelected: (templateId: string | null) => void;
  /** Persist a brand-new template snapshot and select it. */
  create: (name: string, fieldIds: string[], settings: PrintSettings) => Template | null;
  rename: (id: string, name: string) => void;
  /** 把编辑器里改动的字段选择/排版写回模板。没有它，视图模板等于只读快照。 */
  update: (id: string, patch: { fieldIds?: string[]; settings?: PrintSettings }) => void;
  remove: (id: string) => void;
}

export function useTemplates(tableId: string | null): UseTemplatesResult {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [preferences, setPreferences] = useState<UserPreferences>({
    lastTemplateIdByTable: {},
  });

  useEffect(() => {
    setPreferences(loadPreferences());
  }, []);

  useEffect(() => {
    if (!tableId) {
      setTemplates([]);
      return;
    }
    const load = () => setTemplates(loadTemplatesForTable(tableId));
    load();
    // 云同步合并完成后重载
    window.addEventListener('pp-templates-reloaded', load);
    return () => window.removeEventListener('pp-templates-reloaded', load);
  }, [tableId]);

  const selectedId = useMemo(() => {
    if (!tableId) return null;
    const pref = preferences.lastTemplateIdByTable[tableId];
    if (pref && templates.some((t) => t.id === pref)) return pref;
    return null;
  }, [tableId, preferences, templates]);

  const persistTemplates = useCallback(
    (next: Template[]) => {
      if (!tableId) return;
      setTemplates(next);
      saveTemplatesForTable(tableId, next);
    },
    [tableId],
  );

  const setSelected = useCallback(
    (templateId: string | null) => {
      if (!tableId) return;
      setPreferences((prev) => {
        const map = { ...prev.lastTemplateIdByTable };
        if (templateId == null) {
          delete map[tableId];
        } else {
          map[tableId] = templateId;
        }
        const next: UserPreferences = { lastTemplateIdByTable: map };
        savePreferences(next);
        return next;
      });
    },
    [tableId],
  );

  const create = useCallback(
    (name: string, fieldIds: string[], settings: PrintSettings): Template | null => {
      if (!tableId) return null;
      if (templates.length >= MAX_TEMPLATES_PER_TABLE) {
        try {
          window.dispatchEvent(new CustomEvent('pp-template-limit'));
        } catch {
          /* 非浏览器环境忽略 */
        }
        return null;
      }
      const now = Date.now();
      const template: Template = {
        id: makeId(),
        tableId,
        name,
        fieldIds,
        settings,
        createdAt: now,
        updatedAt: now,
      };
      const next = [template, ...templates];
      persistTemplates(next);
      setSelected(template.id);
      return template;
    },
    [tableId, templates, persistTemplates, setSelected],
  );

  const rename = useCallback(
    (id: string, name: string) => {
      const next = templates.map((t) =>
        t.id === id ? { ...t, name, updatedAt: Date.now() } : t,
      );
      persistTemplates(next);
    },
    [templates, persistTemplates],
  );

  const update = useCallback(
    (id: string, patch: { fieldIds?: string[]; settings?: PrintSettings }) => {
      let changed = false;
      const next = templates.map((t) => {
        if (t.id !== id) return t;
        const merged = { ...t, ...patch, updatedAt: Date.now() };
        // 内容没变就别写 —— 编辑器里每次渲染都可能触发一次同步
        if (
          JSON.stringify({ f: t.fieldIds, s: t.settings }) ===
          JSON.stringify({ f: merged.fieldIds, s: merged.settings })
        ) {
          return t;
        }
        changed = true;
        return merged;
      });
      if (changed) persistTemplates(next);
    },
    [templates, persistTemplates],
  );

  const remove = useCallback(
    (id: string) => {
      const next = templates.filter((t) => t.id !== id);
      persistTemplates(next);
      if (selectedId === id) {
        const fallback = next[0]?.id ?? null;
        setSelected(fallback);
      }
    },
    [templates, persistTemplates, selectedId, setSelected],
  );

  return { templates, selectedId, setSelected, create, rename, update, remove };
}
